import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// EXPERIMENT 6: Salts - Formation
// Exploring six different methods of salt formation
// ============================================================================

// Color codes for salts formation experiment
export const saltFormationColors = {
  // Reagent Colors
  copperWireClean: '#B87333',
  copperWireTarnished: '#8B4513',
  sulfurPowder: '#FFFF00',
  sulfurMelting: '#FFA500',
  copperSulfide: '#2F4F4F',
  hcl: '#FFFFFF',
  naoh: '#FFFFFF',
  nacl: '#FFFFFF',
  cao: '#F5F5F5',
  cuo: '#1A1A1A',
  hno3: '#FFFFFF',
  caNO32: '#FFFFFF',
  cuNO32: '#0066CC',
  nahco3: '#FFFFFF',
  na2co3: '#FFFFFF',
  h2so4: '#FFFFFF',
  ch3cooh: '#FFFFFF',
  cuso4: '#0080FF',
  na2s: '#FFFACD',
  agno3: '#FFFFFF',
  // Metal Colors
  copper: '#B87333',
  zinc: '#A0A0A0',
  tin: '#C0C0C0',
  aluminum: '#D3D3D3',
  iron: '#696969',
  // Precipitate Colors
  cus: '#1A1A1A',
  caco3: '#FFFFFF',
  agcl: '#F5F5F5',
  baso4: '#FAFAFA',
  // Indicator Colors
  litmusRed: '#FF0000',
  litmusBlue: '#0000FF',
  // Flame colors
  innerFlame: '#0088FF',
  outerFlame: '#FFA500',
  // UI colors
  crucible: '#8B8B83',
  clayTriangle: '#D2691E',
  steelWool: '#C0C0C0',
};

// Metal reactivity data
export interface MetalReactivityData {
  id: string;
  symbol: string;
  name: string;
  color: string;
  reactsWithWater: boolean;
  reactsWithHCl: boolean;
  reactionIntensity: 'none' | 'slow' | 'moderate' | 'vigorous';
}

export const metalReactivity: Record<string, MetalReactivityData> = {
  cu: {
    id: 'cu',
    symbol: 'Cu',
    name: 'Copper',
    color: '#B87333',
    reactsWithWater: false,
    reactsWithHCl: false,
    reactionIntensity: 'none',
  },
  zn: {
    id: 'zn',
    symbol: 'Zn',
    name: 'Zinc',
    color: '#A0A0A0',
    reactsWithWater: false,
    reactsWithHCl: true,
    reactionIntensity: 'vigorous',
  },
  sn: {
    id: 'sn',
    symbol: 'Sn',
    name: 'Tin',
    color: '#C0C0C0',
    reactsWithWater: false,
    reactsWithHCl: true,
    reactionIntensity: 'slow',
  },
  al: {
    id: 'al',
    symbol: 'Al',
    name: 'Aluminum',
    color: '#D3D3D3',
    reactsWithWater: false,
    reactsWithHCl: true,
    reactionIntensity: 'vigorous',
  },
  fe: {
    id: 'fe',
    symbol: 'Fe',
    name: 'Iron',
    color: '#696969',
    reactsWithWater: false,
    reactsWithHCl: true,
    reactionIntensity: 'moderate',
  },
};

// Double displacement reaction data
export interface DoubleDisplacementData {
  id: number;
  reactant1: string;
  reactant2: string;
  precipitate: string | null;
  precipitateFormula: string | null;
  precipitateColor: string | null;
  equation: string;
}

export const doubleDisplacementReactions: DoubleDisplacementData[] = [
  {
    id: 1,
    reactant1: 'NaCl(aq)',
    reactant2: 'KNO3(aq)',
    precipitate: null,
    precipitateFormula: null,
    precipitateColor: null,
    equation: 'NaCl + KNO3 -> No reaction (all products soluble)',
  },
  {
    id: 2,
    reactant1: 'CuSO4(aq)',
    reactant2: 'Na2S(aq)',
    precipitate: 'Black precipitate',
    precipitateFormula: 'CuS',
    precipitateColor: '#1A1A1A',
    equation: 'CuSO4 + Na2S -> CuS(s) + Na2SO4',
  },
  {
    id: 3,
    reactant1: 'CaCl2(aq)',
    reactant2: 'Na2CO3(aq)',
    precipitate: 'White precipitate',
    precipitateFormula: 'CaCO3',
    precipitateColor: '#FFFFFF',
    equation: 'CaCl2 + Na2CO3 -> CaCO3(s) + 2NaCl',
  },
  {
    id: 4,
    reactant1: 'NaCl(aq)',
    reactant2: 'AgNO3(aq)',
    precipitate: 'White precipitate',
    precipitateFormula: 'AgCl',
    precipitateColor: '#F5F5F5',
    equation: 'NaCl + AgNO3 -> AgCl(s) + NaNO3',
  },
  {
    id: 5,
    reactant1: 'BaCl2(aq)',
    reactant2: 'Na2SO4(aq)',
    precipitate: 'White precipitate',
    precipitateFormula: 'BaSO4',
    precipitateColor: '#FAFAFA',
    equation: 'BaCl2 + Na2SO4 -> BaSO4(s) + 2NaCl',
  },
];

// Part A: Direct Combination of Elements
const partASteps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Examine Copper Wire',
    shortDescription: 'Obtain about a 5 cm length of copper wire, observe its lustre, malleability and colour',
    handler: 'handleExamineCopper',
    infoContent: {
      title: 'Copper Metal Properties',
      content: `Copper is a transition metal with characteristic properties:
- Lustrous (shiny) reddish-orange color
- Malleable (can be hammered into sheets)
- Ductile (can be drawn into wires)
- Excellent conductor of electricity and heat`,
      whatHappens: 'A piece of copper wire is examined to note its metallic properties before it is used in the direct combination reaction with sulfur.',
      observe: [
        'Note the reddish-orange metallic lustre of copper',
        'Test the malleability by bending the wire',
        'The wire may appear tarnished (dull brown) if oxidized by air',
      ],
    },
  },
  {
    id: 2,
    title: 'Clean Copper Wire',
    shortDescription: 'Use steel wool to clean tarnished copper',
    handler: 'handleCleanCopper',
    validation: {
      requires: ['copperExamined'],
    },
  },
  {
    id: 3,
    title: 'Prepare Copper Coil',
    shortDescription: 'Make a small coil of the Cu wire by wrapping it around a pencil',
    handler: 'handlePrepareCoil',
    validation: {
      requires: ['copperCleaned'],
    },
  },
  {
    id: 4,
    title: 'Add Copper to Crucible',
    shortDescription: 'Place copper coil in crucible (in fume hood)',
    handler: 'handleAddCopperToCrucible',
    validation: {
      requires: ['coilPrepared'],
    },
  },
  {
    id: 5,
    title: 'Add Sulfur',
    shortDescription: 'Add enough powdered sulphur to give a uniform depth of about 3 mm',
    handler: 'handleAddSulfur',
    validation: {
      requires: ['copperInCrucible'],
    },
    infoContent: {
      title: 'Direct Combination of Elements',
      content: `**NOTE:** SULPHUR BURNS IN AIR TO GIVE OFF NOXIOUS SO₂ GAS SO PERFORM THIS REACTION IN A FUME HOOD. Make sure your setup is in the fume hood and the sash is lowered to its safest working level.

When two elements combine directly to form a compound, this is called a synthesis or combination reaction. Place the wire coil in a crucible. Add enough powdered sulphur to give a uniform depth of about 3 mm. Place on a clay triangle (supported by a ring clamp) for heating.`,
      chemicalEquation: '16Cu + S8 -> 8Cu2S  or  8Cu + S8 -> 8CuS',
      whatHappens: 'Powdered sulfur is added to cover the copper coil in the crucible; upon heating, sulfur will melt and react directly with copper to form copper sulfide.',
      observe: [
        'The yellow powdered sulfur covers the copper coil uniformly (~3 mm depth)',
        'Ensure this setup is in the fume hood before heating',
        'The crucible sits on a clay triangle supported by a ring clamp',
      ],
    },
  },
  {
    id: 6,
    title: 'Heat with Bunsen Burner',
    shortDescription: 'Heat crucible at first with low heat on all sides, then heat bottom to red hot until all sulphur is burned off',
    handler: 'handleHeatCrucible',
    validation: {
      requires: ['sulfurAdded'],
    },
  },
  {
    id: 7,
    title: 'Observe Reaction',
    shortDescription: 'Watch sulfur melt and react with copper',
    handler: 'handleObservePartAReaction',
    validation: {
      requires: ['crucibleHeated'],
    },
  },
  {
    id: 8,
    title: 'Allow to Cool',
    shortDescription: 'Let the crucible cool completely',
    handler: 'handleCoolCrucible',
    validation: {
      requires: ['partAReactionObserved'],
    },
  },
  {
    id: 9,
    title: 'Test with Ammonium Hydroxide',
    shortDescription: 'Place small portion in test tube, add 2 mL of 6 M NH4OH, heat gently with Bunsen burner in fume hood',
    handler: 'handleTestWithNH4OH',
    validation: {
      requires: ['crucibleCooled'],
    },
    infoContent: {
      title: 'Distinguishing CuS from Cu2S',
      content: `Copper(II) sulfide, CuS, is insoluble in ammonium hydroxide (NH₄OH), whereas copper(I) sulfide, Cu₂S, dissolves (reacts) to give a blue solution.

On the basis of the preceding information, determine the formula of the compound formed. Write a reaction equation for the formation of the salt (elemental sulphur is S₈).

16Cu + S₈ → 8Cu₂S or 8Cu + S₈ → 8CuS`,
      whatHappens: 'A portion of the copper sulfide product is treated with NH4OH to distinguish between CuS (insoluble) and Cu2S (dissolves to give a blue solution).',
      observe: [
        'If the product dissolves in NH4OH giving a blue solution, it is Cu2S',
        'If the product remains as a dark insoluble solid, it is CuS',
        'Heat gently in the fume hood while observing any color change in the liquid',
      ],
    },
  },
  {
    id: 10,
    title: 'Identify Product',
    shortDescription: 'Based on solubility, identify the copper sulfide formed',
    handler: 'handleIdentifyProduct',
    validation: {
      requires: ['nh4ohTested'],
    },
  },
];

// Part B: Neutralization (Acid + Base)
const partBSteps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare Acid',
    shortDescription: 'Pour 15 mL of 0.5 M HCl into beaker 1',
    handler: 'handlePrepareAcid',
    infoContent: {
      title: 'Acid-Base Neutralization',
      content: `When an acid reacts with a base, they neutralize each other to form a salt and water:

HCl + NaOH -> NaCl + H2O

This reaction is exothermic - you will feel heat released. The salt (NaCl) remains when water is evaporated.`,
      chemicalEquation: 'HCl(aq) + NaOH(aq) -> NaCl(aq) + H2O(l)',
      whatHappens: 'Hydrochloric acid is measured into a beaker as the first component of the neutralization reaction that will produce sodium chloride salt.',
      observe: [
        'Measure exactly 15 mL of 0.5 M HCl into a clean beaker',
        'The acid solution is colorless',
        'Handle carefully as HCl is corrosive',
      ],
    },
  },
  {
    id: 2,
    title: 'Prepare Base',
    shortDescription: 'Pour 15 mL of 0.5 M NaOH into beaker 2',
    handler: 'handlePrepareBase',
    validation: {
      requires: ['acidPrepared'],
    },
  },
  {
    id: 3,
    title: 'Mix Solutions',
    shortDescription: 'Combine 15 mL of the acid with 15 mL of the base, note temperature change',
    handler: 'handleMixAcidBase',
    validation: {
      requires: ['basePrepared'],
    },
  },
  {
    id: 4,
    title: 'Observe Temperature',
    shortDescription: 'Note the exothermic nature of the reaction',
    handler: 'handleObserveTemperature',
    validation: {
      requires: ['solutionsMixed'],
    },
  },
  {
    id: 5,
    title: 'Test pH',
    shortDescription: 'Using a dropper, add acid or base until neutral (red and blue litmus both unchanged)',
    handler: 'handleTestNeutralPH',
    validation: {
      requires: ['temperatureObserved'],
    },
  },
  {
    id: 6,
    title: 'Evaporate to Dryness',
    shortDescription: 'Place small amount of neutral solution in evaporating dish, place on hot plate and evaporate to dryness',
    handler: 'handleEvaporate',
    validation: {
      requires: ['phNeutral'],
    },
  },
  {
    id: 7,
    title: 'Observe Salt Crystals',
    shortDescription: 'Observe white NaCl crystals remaining',
    handler: 'handleObserveSaltCrystals',
    validation: {
      requires: ['evaporationComplete'],
    },
  },
];

// Part C: Acid + Oxide
const partCSteps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare Oxide Samples',
    shortDescription: 'Add a match-head sized amount of CaO (lime) to tube 1 and CuO (copper oxide) to tube 2',
    handler: 'handlePrepareOxides',
    infoContent: {
      title: 'Metal Oxides as Bases',
      content: `Metal oxides react with acids in a manner similar to metal hydroxides:

CaO + 2HNO3 -> Ca(NO3)2 + H2O
CuO + 2HNO3 -> Cu(NO3)2 + H2O

The blue color of Cu(NO3)2 is characteristic of Cu2+ ions in solution.`,
      chemicalEquation: 'MO + 2HX -> MX2 + H2O',
      whatHappens: 'Small amounts of white CaO and black CuO are placed in separate test tubes for reaction with nitric acid to form salt solutions.',
      observe: [
        'CaO is a white powder (lime); CuO is a fine black powder',
        'Use match-head sized amounts of each oxide',
        'Label the test tubes clearly to track which oxide is in each',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Nitric Acid',
    shortDescription: 'Pour about 1 mL of 3 M HNO3 to each tube; if necessary, add more to dissolve the oxides',
    handler: 'handleAddHNO3ToOxides',
    validation: {
      requires: ['oxidesPrepared'],
    },
  },
  {
    id: 3,
    title: 'Shake Gently',
    shortDescription: 'Mix the solutions gently',
    handler: 'handleShakeOxideTubes',
    validation: {
      requires: ['hno3AddedToOxides'],
    },
  },
  {
    id: 4,
    title: 'Observe Results',
    shortDescription: 'Note the colorless and blue solutions formed',
    handler: 'handleObserveOxideResults',
    validation: {
      requires: ['oxideTubesShaken'],
    },
  },
  {
    id: 5,
    title: 'Record Observations',
    shortDescription: 'Record evidence of chemical reactions',
    handler: 'handleRecordOxideResults',
    validation: {
      requires: ['oxideResultsObserved'],
    },
  },
];

// Part D: Acid + Carbonate
const partDSteps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Test NaHCO3 with HCl',
    shortDescription: 'Place ~1 g NaHCO₃ in test tube, then add 5 mL 0.5 M HCl',
    handler: 'handleNaHCO3Test',
    infoContent: {
      title: 'Carbonates React with Acids',
      content: `Carbonates and bicarbonates react with acids to release CO2 gas:

NaHCO3 + HCl -> NaCl + H2O + CO2(g)
Na2CO3 + 2HCl -> 2NaCl + H2O + CO2(g)

The bubbles you see are carbon dioxide. This gas extinguishes a flame because it doesn't support combustion.`,
      chemicalEquation: 'NaHCO3 + HCl -> NaCl + H2O + CO2(g)',
      whatHappens: 'Sodium bicarbonate reacts with hydrochloric acid to produce sodium chloride, water, and carbon dioxide gas that visibly bubbles from the solution.',
      observe: [
        'Vigorous effervescence (bubbling) occurs when HCl contacts NaHCO3',
        'The white NaHCO3 solid dissolves as the reaction proceeds',
        'The evolved gas is colorless CO2 that does not support combustion',
      ],
    },
  },
  {
    id: 2,
    title: 'Observe Bubbling',
    shortDescription: 'Watch the gas evolution',
    handler: 'handleObserveNaHCO3Bubbles',
    validation: {
      requires: ['nahco3TestStarted'],
    },
  },
  {
    id: 3,
    title: 'Flame Test (Combustion Support)',
    shortDescription: 'Test any gas evolved by inserting a lit taper into the test tube',
    handler: 'handleFlameTest',
    validation: {
      requires: ['bubblesObserved'],
    },
  },
  {
    id: 4,
    title: 'Observe Flame Extinguish',
    shortDescription: 'Confirm CO2 by flame extinguishing',
    handler: 'handleObserveFlameExtinguish',
    validation: {
      requires: ['flameTestDone'],
    },
  },
  {
    id: 5,
    title: 'Compare H2SO4 vs Acetic Acid',
    shortDescription: 'Fill two tubes 1/3 full: one with 0.5 M H2SO4, other with 0.5 M acetic acid. Drop a few crystals of Na2CO3 into each.',
    handler: 'handleCompareAcids',
    validation: {
      requires: ['flameExtinguished'],
    },
  },
  {
    id: 6,
    title: 'Observe Reaction Rates',
    shortDescription: 'Compare vigorous vs slow bubbling',
    handler: 'handleObserveReactionRates',
    validation: {
      requires: ['acidsCompared'],
    },
  },
  {
    id: 7,
    title: 'Record Observations',
    shortDescription: 'Record differences between acid strengths',
    handler: 'handleRecordAcidComparison',
    validation: {
      requires: ['reactionRatesObserved'],
    },
  },
];

// Part E: Acid + Metal
const partESteps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare Metal Samples',
    shortDescription: 'Obtain small amounts (roughly match-head size) of Cu, Zn, Sn, Al, Fe, place each in a labelled test tube',
    handler: 'handlePrepareMetals',
    infoContent: {
      title: 'Metal Activity Series',
      content: `Metals can be ranked by their reactivity. More active metals will react more vigorously with acids, displacing hydrogen gas:

Zn + 2HCl -> ZnCl2 + H2(g)

The order of activity (most to least reactive):
Li > K > Ca > Na > Mg > Al > Zn > Fe > Ni > Sn > Pb > H > Cu > Ag > Au

Copper doesn't react with HCl because it's below hydrogen in the series.`,
      chemicalEquation: 'Zn + 2HCl -> ZnCl2 + H2(g)',
      whatHappens: 'Small samples of five metals (Cu, Zn, Sn, Al, Fe) are placed in labeled test tubes to compare their reactivity with water and hydrochloric acid.',
      observe: [
        'Note the different appearances: Cu is reddish, Zn is gray, Sn is silvery, Al is light gray, Fe is dark gray',
        'Use match-head sized amounts of each metal',
        'Label each tube clearly with the metal symbol',
      ],
    },
  },
  {
    id: 2,
    title: 'Test with Water',
    shortDescription: 'In fume hood, pour about 2 mL deionised water into each test tube',
    handler: 'handleTestWithWater',
    validation: {
      requires: ['metalsPrepared'],
    },
  },
  {
    id: 3,
    title: 'Observe for 2 Minutes',
    shortDescription: 'Watch for any evidence of reaction with water over about 2 minutes',
    handler: 'handleObserveWaterReaction',
    validation: {
      requires: ['waterAdded'],
    },
  },
  {
    id: 4,
    title: 'Decant Water',
    shortDescription: 'Remove water from each tube',
    handler: 'handleDecantWater',
    validation: {
      requires: ['waterReactionObserved'],
    },
  },
  {
    id: 5,
    title: 'Add HCl',
    shortDescription: 'After observing water reaction, add 6M HCl to each test tube',
    handler: 'handleAddHClToMetals',
    validation: {
      requires: ['waterDecanted'],
    },
  },
  {
    id: 6,
    title: 'Observe Reactions',
    shortDescription: 'Compare reaction intensity for each metal',
    handler: 'handleObserveHClReactions',
    validation: {
      requires: ['hclAddedToMetals'],
    },
  },
  {
    id: 7,
    title: 'Rank by Reactivity',
    shortDescription: 'Arrange metals in order of activity: a) with water, b) with HCl. Check activity table (Table II in lab 7).',
    handler: 'handleRankMetals',
    validation: {
      requires: ['hclReactionsObserved'],
    },
  },
];

// Part F: Double Displacement
const partFSteps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare Test Tubes',
    shortDescription: 'Obtain 5 clean test tubes numbered 1 to 5. Add about 2 mL of 0.1 M solutions of each of the two reactants.',
    handler: 'handlePrepareDoubleDispTubes',
    infoContent: {
      title: 'Predicting Precipitates',
      content: `In double displacement reactions, ions exchange partners. A precipitate forms if the new combination is insoluble:

CuSO₄ + Na₂S -> CuS(s) + Na₂SO₄

Describe the results of your experiments, including any instances where there was no evidence of a reaction. Write equations for the reactions that took place, giving the name of the salt formed in each case. Use the **solubility table (Table I in lab 7)** to complete the equations.`,
      whatHappens: 'Five pairs of ionic solutions are mixed to observe double displacement reactions; precipitates form when the product ion combination is insoluble.',
      observe: [
        'Prepare 5 clean test tubes and add about 2 mL of each reactant solution',
        'Look for precipitate formation, color changes, or no visible reaction',
        'Use the solubility table to predict which combinations should form precipitates',
      ],
    },
  },
  {
    id: 2,
    title: 'Reaction 1: NaCl + KNO3',
    shortDescription: 'Mix NaCl and KNO3 solutions',
    handler: 'handleReaction1',
    validation: {
      requires: ['doubleDispTubesPrepared'],
    },
  },
  {
    id: 3,
    title: 'Observe Reaction 1',
    shortDescription: 'Note if any precipitate forms',
    handler: 'handleObserveReaction1',
    validation: {
      requires: ['reaction1Done'],
    },
  },
  {
    id: 4,
    title: 'Reaction 2: CuSO4 + Na2S',
    shortDescription: 'Mix CuSO4 and Na2S solutions',
    handler: 'handleReaction2',
    validation: {
      requires: ['reaction1Observed'],
    },
  },
  {
    id: 5,
    title: 'Observe Reaction 2',
    shortDescription: 'Note the black CuS precipitate',
    handler: 'handleObserveReaction2',
    validation: {
      requires: ['reaction2Done'],
    },
  },
  {
    id: 6,
    title: 'Reaction 3: CaCl2 + Na2CO3',
    shortDescription: 'Mix CaCl2 and Na2CO3 solutions',
    handler: 'handleReaction3',
    validation: {
      requires: ['reaction2Observed'],
    },
  },
  {
    id: 7,
    title: 'Observe Reaction 3',
    shortDescription: 'Note the white CaCO3 precipitate',
    handler: 'handleObserveReaction3',
    validation: {
      requires: ['reaction3Done'],
    },
  },
  {
    id: 8,
    title: 'Reaction 4: NaCl + AgNO3',
    shortDescription: 'Mix NaCl and AgNO3 solutions',
    handler: 'handleReaction4',
    validation: {
      requires: ['reaction3Observed'],
    },
  },
  {
    id: 9,
    title: 'Observe Reaction 4',
    shortDescription: 'Note the white AgCl precipitate',
    handler: 'handleObserveReaction4',
    validation: {
      requires: ['reaction4Done'],
    },
  },
  {
    id: 10,
    title: 'Reaction 5: BaCl2 + Na2SO4',
    shortDescription: 'Mix BaCl2 and Na2SO4 solutions',
    handler: 'handleReaction5',
    validation: {
      requires: ['reaction4Observed'],
    },
  },
  {
    id: 11,
    title: 'Observe Reaction 5',
    shortDescription: 'Note the white BaSO4 precipitate',
    handler: 'handleObserveReaction5',
    validation: {
      requires: ['reaction5Done'],
    },
  },
  {
    id: 12,
    title: 'Write Equations',
    shortDescription: 'Write balanced equations for each reaction',
    handler: 'handleWriteEquations',
    validation: {
      requires: ['reaction5Observed'],
    },
  },
];

// Initial States
const partAInitialState = {
  copperExamined: false,
  copperCleaned: false,
  coilPrepared: false,
  copperInCrucible: false,
  sulfurAdded: false,
  crucibleHeated: false,
  partAReactionObserved: false,
  crucibleCooled: false,
  nh4ohTested: false,
  productIdentified: null as string | null,
  copperColor: saltFormationColors.copperWireTarnished,
  sulfurState: 'powder',
  productColor: saltFormationColors.copperSulfide,
};

const partBInitialState = {
  acidPrepared: false,
  basePrepared: false,
  solutionsMixed: false,
  temperatureObserved: false,
  temperature: 25,
  phNeutral: false,
  evaporationComplete: false,
  saltCrystalsObserved: false,
};

const partCInitialState = {
  oxidesPrepared: false,
  hno3AddedToOxides: false,
  oxideTubesShaken: false,
  oxideResultsObserved: false,
  caoColor: saltFormationColors.cao,
  cuoColor: saltFormationColors.cuo,
  caNO32Color: saltFormationColors.caNO32,
  cuNO32Color: saltFormationColors.cuNO32,
};

const partDInitialState = {
  nahco3TestStarted: false,
  bubblesObserved: false,
  flameTestDone: false,
  flameExtinguished: false,
  acidsCompared: false,
  reactionRatesObserved: false,
  h2so4Rate: 'vigorous',
  aceticRate: 'slow',
};

const partEInitialState = {
  metalsPrepared: false,
  waterAdded: false,
  waterReactionObserved: false,
  waterDecanted: false,
  hclAddedToMetals: false,
  hclReactionsObserved: false,
  reactivityRanked: false,
  metalReactions: {} as Record<string, { water: boolean; hcl: boolean; intensity: string }>,
};

const partFInitialState = {
  doubleDispTubesPrepared: false,
  reaction1Done: false,
  reaction1Observed: false,
  reaction2Done: false,
  reaction2Observed: false,
  reaction3Done: false,
  reaction3Observed: false,
  reaction4Done: false,
  reaction4Observed: false,
  reaction5Done: false,
  reaction5Observed: false,
  equationsWritten: false,
  precipitates: {
    1: null as string | null,
    2: '#1A1A1A',
    3: '#FFFFFF',
    4: '#F5F5F5',
    5: '#FAFAFA',
  },
};

// Part Definitions
const partA: ExperimentPart = {
  id: 1,
  name: 'directCombination',
  title: 'Part A: Direct Combination of Elements',
  steps: partASteps,
  equipment: ['Copper wire (5 cm)', 'Steel wool', 'Pencil (for coiling wire)', 'Crucible', 'Clay triangle', 'Ring clamp', 'Bunsen burner', 'Powdered sulphur', '6 M NH₄OH', 'Test tube', 'Fume hood'],
  initialState: partAInitialState,
};

const partB: ExperimentPart = {
  id: 2,
  name: 'neutralization',
  title: 'Part B: Neutralization (Acid + Base)',
  steps: partBSteps,
  equipment: ['50 mL beakers (2)', '0.5 M HCl', '0.5 M NaOH', 'Litmus paper', 'Evaporating dish', 'Hot plate'],
  initialState: partBInitialState,
};

const partC: ExperimentPart = {
  id: 3,
  name: 'acidOxide',
  title: 'Part C: Acid + Oxide',
  steps: partCSteps,
  equipment: ['Test tubes (2)', 'CaO (lime)', 'CuO', '3 M HNO3'],
  initialState: partCInitialState,
};

const partD: ExperimentPart = {
  id: 4,
  name: 'acidCarbonate',
  title: 'Part D: Acid + Carbonate',
  steps: partDSteps,
  equipment: ['Test tubes', 'NaHCO₃ (~1 g)', 'Na₂CO₃ (a few crystals)', '0.5 M HCl (5 mL)', '0.5 M H₂SO₄', '0.5 M acetic acid (CH₃COOH)', 'Taper/match'],
  initialState: partDInitialState,
};

const partE: ExperimentPart = {
  id: 5,
  name: 'acidMetal',
  title: 'Part E: Acid + Metal',
  steps: partESteps,
  equipment: ['Test tubes (5)', 'Cu', 'Zn', 'Sn', 'Al', 'Fe', 'Deionized water', '6 M HCl'],
  initialState: partEInitialState,
};

const partF: ExperimentPart = {
  id: 6,
  name: 'doubleDisplacement',
  title: 'Part F: Double Displacement',
  steps: partFSteps,
  equipment: ['Test tubes (5)', '0.1 M NaCl', '0.1 M KNO₃', '0.1 M CuSO₄', '0.1 M Na₂S', '0.1 M CaCl₂', '0.1 M Na₂CO₃', '0.1 M AgNO₃', '0.1 M BaCl₂', '0.1 M Na₂SO₄'],
  initialState: partFInitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

const experiment6Config: ExperimentConfig = {
  id: 'exp6',
  number: 6,
  name: 'Salts - Formation',
  description: 'Explore six different methods of salt formation: direct combination, neutralization, acid-oxide, acid-carbonate, acid-metal, and double displacement reactions.',
  estimatedTime: '150 minutes',
  learningObjectives: [
    'Understand salt formation through direct combination of elements',
    'Observe and understand neutralization reactions (acid + base)',
    'Learn how metal oxides react with acids to form salts',
    'Observe gas evolution from carbonate-acid reactions',
    'Understand the activity series of metals and displacement reactions',
    'Predict and observe precipitates in double displacement reactions',
    'Write balanced chemical equations for salt formation reactions',
  ],
  safetyNotes: [
    'Good housekeeping is essential - clean up any spilled materials in or near the balance immediately',
    'Handle sample material with a clean spatula only - never return anything to the bottle',
    'SULPHUR BURNS IN AIR TO GIVE OFF NOXIOUS SO₂ GAS - perform reaction (a) in a fume hood',
    'Make sure fume hood sash is lowered to its safest working level',
    'Wear safety goggles at all times',
    'Handle concentrated acids with care',
    'Some reactions produce flammable H₂ gas',
    'NH₄OH has a strong odor - use in ventilation',
    'AgNO₃ stains skin - avoid contact',
    'No metals are to be disposed of down the sink',
    'Dispose of silver nitrate waste into its own unique waste container',
    'Dispose of all other metals into the metals waste container',
    'Disposal: Ammonium hydroxide, hydrochloric acid, sodium hydroxide, nitric acid',
    'Wash hands thoroughly after the experiment',
  ],
  requiredEquipment: [
    'Fume hood',
    'Crucible and clay triangle',
    'Ring clamp and Bunsen burner',
    'Copper wire (5 cm)',
    'Steel wool',
    'Pencil (for coiling)',
    'Powdered sulphur',
    'Test tubes (many)',
    'Beakers (50 mL)',
    'Evaporating dish',
    'Hot plate',
    'Various acids (0.5 M HCl, 3 M HNO₃, 0.5 M H₂SO₄, 0.5 M CH₃COOH, 6 M HCl)',
    'Various bases (0.5 M NaOH, 6 M NH₄OH)',
    'Metal oxides (CaO, CuO)',
    'Carbonates (NaHCO₃, Na₂CO₃)',
    'Metals (Cu, Zn, Sn, Al, Fe - match-head sized amounts)',
    '0.1 M solutions for double displacement (NaCl, KNO₃, CuSO₄, Na₂S, CaCl₂, Na₂CO₃, AgNO₃, BaCl₂, Na₂SO₄)',
    'Litmus paper',
    'Taper/matches',
  ],
  parts: [partA, partB, partC, partD, partE, partF],
  quiz: {
    experimentId: 'exp6',
    title: 'Salt Formation Quiz',
    questions: [
      {
        id: 'exp6-q1',
        type: 'mcq' as const,
        question: 'Which method of salt formation involves combining a metal and a non-metal directly? For example, heating copper wire in sulfur vapor.',
        options: [
          { id: 'a', text: 'Neutralization' },
          { id: 'b', text: 'Direct combination (synthesis)' },
          { id: 'c', text: 'Double displacement' },
          { id: 'd', text: 'Acid-carbonate reaction' },
        ],
        correctOptionId: 'b',
        explanation: 'Direct combination (synthesis) involves combining elements directly to form a compound. For example: Cu + S → CuS. The copper wire is heated and plunged into sulfur to form copper(II) sulfide.',
      },
      {
        id: 'exp6-q2',
        type: 'mcq' as const,
        question: 'When CaO (a metal oxide) reacts with HCl, what type of salt formation method is this?',
        options: [
          { id: 'a', text: 'Direct combination' },
          { id: 'b', text: 'Acid-metal reaction' },
          { id: 'c', text: 'Acid-oxide reaction' },
          { id: 'd', text: 'Double displacement' },
        ],
        correctOptionId: 'c',
        explanation: 'When a metal oxide reacts with an acid, a salt and water are formed: CaO + 2HCl → CaCl₂ + H₂O. This is the acid-oxide method of salt formation.',
      },
      {
        id: 'exp6-q3',
        type: 'mcq' as const,
        question: 'In double displacement reactions, a precipitate forms when:',
        options: [
          { id: 'a', text: 'Both products are soluble' },
          { id: 'b', text: 'One of the products is insoluble in water' },
          { id: 'c', text: 'A gas is always evolved' },
          { id: 'd', text: 'The reaction is endothermic' },
        ],
        correctOptionId: 'b',
        explanation: 'In a double displacement reaction, a precipitate forms when one of the product salts is insoluble. For example: CuSO₄ + Na₂S → CuS\u2193 + Na₂SO₄. CuS is insoluble and precipitates out of solution.',
      },
      {
        id: 'exp6-q4',
        type: 'short_answer' as const,
        question: 'Write the balanced equation for the reaction of sodium carbonate (Na₂CO₃) with hydrochloric acid (HCl), and explain the observation you would see.',
        sampleAnswer: 'Na₂CO₃ + 2HCl → 2NaCl + H₂O + CO₂\u2191. You would observe vigorous fizzing or effervescence due to the evolution of carbon dioxide gas. The solid carbonate dissolves as the reaction proceeds, and the resulting solution contains dissolved NaCl.',
      },
      {
        id: 'exp6-q5',
        type: 'calculation' as const,
        question: 'If 0.50 g of zinc metal (molar mass = 65.38 g/mol) reacts completely with excess HCl, how many moles of H₂ gas are produced? (Zn + 2HCl → ZnCl₂ + H₂)',
        expectedValue: 0.00765,
        tolerance: 3,
        unit: 'mol',
      },
    ],
  },
};

export default experiment6Config;
